import { Box, Stack, TextField, Typography } from "@mui/material";
import { COLORS } from "assets";
import { ChangeEventHandler, ReactNode, startTransition, useCallback } from "react";

export interface PageToolbarProps {
  title: string;
  search?: string;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  actions?: ReactNode;
  breadcrumbs?: ReactNode;
}

export default function PageToolbar({
  title,
  search,
  searchPlaceholder = "Search",
  onSearchChange,
  actions,
  breadcrumbs
}: PageToolbarProps) {
  const handleSearch: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const value = e.target.value;
      if (!onSearchChange) return;
      startTransition(() => onSearchChange(value));
    },
    [onSearchChange]
  );

  return (
    <Box mb={2}>
      {breadcrumbs}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={1.5}
        mt={breadcrumbs ? 1 : 0}
      >
        <Typography
          variant="h1"
          sx={{ color: COLORS.surface.primaryText, fontSize: { xs: 22, md: 26 }, fontWeight: 600 }}
        >
          {title}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
          {onSearchChange ? (
            <TextField
              size="small"
              value={search ?? ""}
              onChange={handleSearch}
              placeholder={searchPlaceholder}
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                "& .MuiOutlinedInput-root": {
                  bgcolor: COLORS.surface.card,
                  color: COLORS.surface.primaryText,
                  borderRadius: `${COLORS.radius.sm}px`,
                  "& fieldset": { borderColor: COLORS.surface.border },
                  "&:hover fieldset": { borderColor: COLORS.primary },
                  "&.Mui-focused fieldset": { borderColor: COLORS.primary }
                },
                "& .MuiInputBase-input::placeholder": { color: COLORS.surface.mutedText, opacity: 1 }
              }}
            />
          ) : null}
          {actions}
        </Stack>
      </Stack>
    </Box>
  );
}
